/* eslint-disable @typescript-eslint/no-unused-vars */
import { Service } from 'src/models/service/service';
import { Entity } from 'src/models/entity/entity';
import { makeKeyValueRepository } from 'src/data/repositories/key-value/key-value-repository-factory';
import { RepositoryKey } from 'src/models/common/repository/repository-key';
import { makeConfigService } from '../../common/config/config-service-factory';
import { CustomError } from '../../../../helpers/error/custom-error';
import { ResponsePagination } from '../../../../models/common/response/response-pagination/response-pagination';

import { BaseCrudServiceImpl } from '../../common/base-crud/base-crud-service-impl';
import { ILogService } from '../../common/log/log-service';
import { IAuthService } from '../../auth/auth-service';
import { IKeyParams } from 'src/models/common/params/key-params';
import { IdStatus } from 'src/models/common/base/id';
import { SERVICE_ENDPOINTS } from './service-endpoints';
import {
  EntityServiceLink,
  ImportServicesResult,
  IService,
  SyncEntityServicesPayload,
} from './service';
import { SimilarNameCheckResult } from '../common/similar-name-check';
import { makeLoadEntityFromCentralUseCase } from 'src/usecases/central/entity/load-entity-from-central/load-entity-from-central-use-case-factory';
import { LanguageKey } from 'src/main/translate/language-key/language-key';
import {
  isEntitySharingRoot,
  resolveEntitySharingRoot,
} from 'src/helpers/entity/entity-sharing';
import { makeEnvironment } from 'src/environments/make-environment';

enum ServiceSearchKey {
  NAME = 'name',
  PAGE = 'page',
  LIMIT = 'limit',
  SORT = 'sort',
  DIRECTION = 'direction',
  ALL = 'all',
  STATUS = 'status',
  TARGET_ENTITY_ID = 'target_entity_id',
  ONLY_LINKED = 'only_linked',
}

export class ServiceImpl
  extends BaseCrudServiceImpl<Service>
  implements IService
{
  private readonly keyValueRepository = makeKeyValueRepository();
  private readonly configService = makeConfigService();
  private readonly loadEntitiesUseCase = makeLoadEntityFromCentralUseCase();

  constructor(
    private locallogService: ILogService,
    authService: IAuthService,
  ) {
    super(
      SERVICE_ENDPOINTS.base,
      Service.fromJson,
      locallogService,
      authService,
    );
  }

  private async getSelectedEntity(): Promise<Entity | null> {
    const selectedEntity = await this.keyValueRepository.get(
      RepositoryKey.SELECTED_ENTITY,
    );

    return selectedEntity ? (selectedEntity as Entity) : null;
  }

  private isRootEntity(entity: Entity | null | undefined): boolean {
    return isEntitySharingRoot(entity, (taxId) =>
      this.configService.isPrincipalEntityTaxId(taxId),
    );
  }

  private async assertRootEntityAccess(): Promise<Entity> {
    const selectedEntity = await this.getSelectedEntity();

    if (!this.isRootEntity(selectedEntity)) {
      throw CustomError.invalidContent([
        LanguageKey.ONLY_ROOT_ENTITY_CAN_MANAGE_SERVICES,
      ]);
    }

    return selectedEntity as Entity;
  }

  private async resolveCreationContext(): Promise<{
    selectedEntity: Entity;
    ownerRootEntity: Entity;
  }> {
    const selectedEntity = await this.getSelectedEntity();

    if (!selectedEntity?.id) {
      throw CustomError.invalidContent([
        LanguageKey.NO_SELECTED_ENTITY_TO_CREATE_SERVICES,
      ]);
    }

    const isRootSelection = this.isRootEntity(selectedEntity);
    if (isRootSelection) {
      return {
        selectedEntity,
        ownerRootEntity: selectedEntity,
      };
    }

    const entities = await this.loadEntitiesUseCase.handle();
    const ownerRootEntity = resolveEntitySharingRoot(
      entities,
      selectedEntity.id,
      (taxId) => this.configService.isPrincipalEntityTaxId(taxId),
    );

    if (!ownerRootEntity?.id) {
      throw CustomError.invalidContent([
        LanguageKey.COULD_NOT_LOCATE_CONFIGURED_ROOT_ENTITY,
      ]);
    }

    return {
      selectedEntity,
      ownerRootEntity,
    };
  }

  private async appendVisibilityParams(
    params: Record<string, string>,
  ): Promise<Record<string, string>> {
    const selectedEntity = await this.getSelectedEntity();

    const selectedEntityId = selectedEntity?.id?.toString().trim();
    if (!selectedEntityId) {
      throw CustomError.invalidContent([
        LanguageKey.NO_SELECTED_ENTITY_TO_CREATE_SERVICES,
      ]);
    }

    return {
      ...params,
      [ServiceSearchKey.TARGET_ENTITY_ID]: selectedEntityId,
      [ServiceSearchKey.ONLY_LINKED]: 'true',
    };
  }

  override async get(id: string): Promise<Service> {
    try {
      await this.logService.info('ServiceImpl.get -> start');

      const params = await this.appendVisibilityParams({});
      const result = await this.authService.http.get(
        SERVICE_ENDPOINTS.byId(id),
        {
          params,
        },
      );

      return Service.fromJson(result);
    } catch (error) {
      throw CustomError.handleError('ServiceImpl.get -> error', error);
    } finally {
      await this.logService.info('ServiceImpl.get -> end');
    }
  }

  override async search(
    keyword: string,
    page: number,
    limit: number,
    all?: boolean,
    status?: IdStatus,
    direction?: 'asc' | 'desc',
  ): Promise<ResponsePagination> {
    const safeKeyword = keyword?.trim() || '';
    const keys: IKeyParams<ServiceSearchKey>[] = [];
    if (safeKeyword.length > 0) {
      keys.push({ key: ServiceSearchKey.NAME, value: safeKeyword });
    }
    keys.push({ key: ServiceSearchKey.PAGE, value: (page || 1).toString() });
    keys.push({ key: ServiceSearchKey.LIMIT, value: (limit || 10).toString() });
    keys.push({
      key: ServiceSearchKey.DIRECTION,
      value: direction || 'asc',
    });
    if (all) {
      keys.push({ key: ServiceSearchKey.ALL, value: 'true' });
    }
    if (status) {
      keys.push({ key: ServiceSearchKey.STATUS, value: status });
    }

    return this.searchByParams(keys);
  }

  async searchByParams(
    keys: IKeyParams<ServiceSearchKey>[],
  ): Promise<ResponsePagination> {
    try {
      await this.logService.info('ServiceImpl.search -> start');

      const baseParams = Object.fromEntries(keys.map((k) => [k.key, k.value]));
      const params = await this.appendVisibilityParams(baseParams);
      const queryString = new URLSearchParams(
        Object.entries(params).reduce(
          (acc, [key, value]) => {
            acc[key] = String(value);
            return acc;
          },
          {} as Record<string, string>,
        ),
      ).toString();

      const result = await this.authService.http.get(
        `${this.apiPath}?${queryString}`,
      );

      return ResponsePagination.fromJson(result);
    } catch (error) {
      throw CustomError.handleError('ServiceImpl.search -> error', error);
    } finally {
      await this.logService.info('ServiceImpl.search -> end');
    }
  }

  override async create(data: Service): Promise<string> {
    try {
      await this.logService.info('ServiceImpl.create -> start');
      const creationContext = await this.resolveCreationContext();
      const payload = this.toSavePayload(data, {
        owner_root_entity_id:
          data?.owner_root_entity_id || creationContext.ownerRootEntity.id,
        target_entity_id: creationContext.selectedEntity.id,
      });

      const result = await this.authService.http.post(
        `${this.apiPath}`,
        payload,
      );

      return result.id;
    } catch (error) {
      throw CustomError.handleError('ServiceImpl.create -> error', error);
    } finally {
      await this.logService.info('ServiceImpl.create -> end');
    }
  }

  override async update(id: string, data: Service): Promise<void> {
    try {
      await this.logService.info('ServiceImpl.update -> start');
      const selectedRootEntity = await this.assertRootEntityAccess();
      const payload = this.toSavePayload(data, {
        owner_root_entity_id:
          data?.owner_root_entity_id || selectedRootEntity.id,
        target_entity_id: selectedRootEntity.id,
      });

      await this.authService.http.put(SERVICE_ENDPOINTS.byId(id), payload);
    } catch (error) {
      throw CustomError.handleError('ServiceImpl.update -> error', error);
    } finally {
      await this.logService.info('ServiceImpl.update -> end');
    }
  }

  override async delete(id: string): Promise<void> {
    try {
      await this.logService.info('ServiceImpl.delete -> start');
      await this.assertRootEntityAccess();

      await this.authService.http.delete(SERVICE_ENDPOINTS.byId(id));
    } catch (error) {
      throw CustomError.handleError('ServiceImpl.delete -> error', error);
    } finally {
      await this.logService.info('ServiceImpl.delete -> end');
    }
  }

  async checkSimilarName(
    name: string,
    excludeId?: string,
  ): Promise<SimilarNameCheckResult> {
    try {
      await this.logService.info('ServiceImpl.checkSimilarName -> start');
      const creationContext = await this.resolveCreationContext();

      return await this.authService.http.post(
        SERVICE_ENDPOINTS.checkSimilarName,
        {
          name,
          exclude_id: excludeId || undefined,
          owner_root_entity_id: creationContext.ownerRootEntity.id,
        },
      );
    } catch (error) {
      throw CustomError.handleError(
        'ServiceImpl.checkSimilarName -> error',
        error,
      );
    } finally {
      await this.logService.info('ServiceImpl.checkSimilarName -> end');
    }
  }

  async listEntityLinks(entityId: string): Promise<EntityServiceLink[]> {
    try {
      await this.logService.info('ServiceImpl.listEntityLinks -> start');
      await this.assertRootEntityAccess();

      const result = await this.authService.http.get(
        SERVICE_ENDPOINTS.entityLinks(entityId),
      );

      return Array.isArray(result) ? result : [];
    } catch (error) {
      throw CustomError.handleError(
        'ServiceImpl.listEntityLinks -> error',
        error,
      );
    } finally {
      await this.logService.info('ServiceImpl.listEntityLinks -> end');
    }
  }

  async syncEntityLinks(
    entityId: string,
    payload: SyncEntityServicesPayload,
  ): Promise<EntityServiceLink[]> {
    try {
      await this.logService.info('ServiceImpl.syncEntityLinks -> start');
      const selectedRootEntity = await this.assertRootEntityAccess();

      const result = await this.authService.http.put(
        SERVICE_ENDPOINTS.entityLinks(entityId),
        {
          ...payload,
          owner_root_entity_id:
            payload.owner_root_entity_id || selectedRootEntity.id,
        },
      );

      return Array.isArray(result) ? result : [];
    } catch (error) {
      throw CustomError.handleError(
        'ServiceImpl.syncEntityLinks -> error',
        error,
      );
    } finally {
      await this.logService.info('ServiceImpl.syncEntityLinks -> end');
    }
  }

  async importFromSenhas(): Promise<ImportServicesResult> {
    try {
      await this.logService.info('ServiceImpl.importFromSenhas -> start');
      const context = await this.resolveCreationContext();
      const senhasSoftwareId = makeEnvironment().API_SENHAS_SOFTWARE_ID?.trim();

      if (!senhasSoftwareId) {
        throw new Error('apiSenhasSoftwareId is not configured.');
      }

      return await this.authService.http.post(SERVICE_ENDPOINTS.import, {
        target_entity_id: context.selectedEntity.id,
        owner_root_entity_id: context.ownerRootEntity.id,
        senhas_software_id: senhasSoftwareId,
      });
    } catch (error) {
      throw CustomError.handleError('ServiceImpl.importFromSenhas -> error', error);
    } finally {
      await this.logService.info('ServiceImpl.importFromSenhas -> end');
    }
  }

  private toSavePayload(
    data: Service,
    context: { owner_root_entity_id: string; target_entity_id: string },
  ) {
    const formData = data as Service & { status?: IdStatus };

    return {
      name: data.name,
      description: data.description,
      acronym: data.acronym || undefined,
      color: data.color,
      status: formData.status || (data.isActive ? IdStatus.ACTIVE : IdStatus.INACTIVE),
      scheduling_duration_minutes: data.scheduling_duration_minutes || 30,
      parent_service_id: data.parent_service_id || undefined,
      owner_root_entity_id: context.owner_root_entity_id,
      target_entity_id: context.target_entity_id,
    };
  }
}
